import React, { useRef, useEffect, useState } from 'react';

interface PriceRangeSliderProps {
  min: number;
  max: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
}

export function PriceRangeSlider({ min, max, value, onChange }: PriceRangeSliderProps) {
  const [localValue, setLocalValue] = useState<[number, number]>(value);
  const sliderRef = useRef<HTMLDivElement>(null);
  const minThumbRef = useRef<HTMLDivElement>(null);
  const maxThumbRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  // Sync local value with prop value
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleMinInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMin = Math.min(Math.max(Number(e.target.value), min), localValue[1] - 1);
    setLocalValue([newMin, localValue[1]]);
    onChange([newMin, localValue[1]]);
  };

  const handleMaxInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMax = Math.max(Math.min(Number(e.target.value), max), localValue[0] + 1);
    setLocalValue([localValue[0], newMax]);
    onChange([localValue[0], newMax]);
  };

  const getPercent = (value: number): number => {
    return ((value - min) / (max - min)) * 100;
  };

  const updateMinThumbPosition = () => {
    if (minThumbRef.current && trackRef.current) {
      const percent = getPercent(localValue[0]);
      minThumbRef.current.style.left = `${percent}%`;
      trackRef.current.style.left = `${percent}%`;
      trackRef.current.style.width = `${getPercent(localValue[1]) - percent}%`;
    }
  };

  const updateMaxThumbPosition = () => {
    if (maxThumbRef.current && trackRef.current) {
      const percent = getPercent(localValue[1]);
      maxThumbRef.current.style.left = `${percent}%`;
      trackRef.current.style.width = `${percent - getPercent(localValue[0])}%`;
    }
  };

  useEffect(() => {
    updateMinThumbPosition();
    updateMaxThumbPosition();
  }, [localValue, min, max]);

  const handleSliderClick = (e: React.MouseEvent) => {
    if (!sliderRef.current) return;
    
    const rect = sliderRef.current.getBoundingClientRect();
    const clickPosition = e.clientX - rect.left;
    const sliderWidth = rect.width;
    const clickPercent = clickPosition / sliderWidth;
    const clickValue = Math.round(min + clickPercent * (max - min));
    
    // Determine which thumb to move based on which is closer
    const minDistance = Math.abs(clickValue - localValue[0]);
    const maxDistance = Math.abs(clickValue - localValue[1]);
    
    if (minDistance <= maxDistance) {
      // Move min thumb
      const newMin = Math.min(Math.max(clickValue, min), localValue[1] - 1);
      setLocalValue([newMin, localValue[1]]);
      onChange([newMin, localValue[1]]);
    } else {
      // Move max thumb
      const newMax = Math.max(Math.min(clickValue, max), localValue[0] + 1);
      setLocalValue([localValue[0], newMax]);
      onChange([localValue[0], newMax]);
    }
  };

  const handleMinThumbMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    const startX = e.clientX;
    const startValue = localValue[0];
    const sliderRect = sliderRef.current?.getBoundingClientRect();
    
    if (!sliderRect) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startX;
      const deltaPercent = deltaX / sliderRect.width;
      const deltaValue = deltaPercent * (max - min);
      const newMin = Math.min(Math.max(startValue + Math.round(deltaValue), min), localValue[1] - 1);
      
      setLocalValue([newMin, localValue[1]]);
      onChange([newMin, localValue[1]]);
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMaxThumbMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    const startX = e.clientX;
    const startValue = localValue[1];
    const sliderRect = sliderRef.current?.getBoundingClientRect();
    
    if (!sliderRect) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startX;
      const deltaPercent = deltaX / sliderRect.width;
      const deltaValue = deltaPercent * (max - min);
      const newMax = Math.max(Math.min(startValue + Math.round(deltaValue), max), localValue[0] + 1);
      
      setLocalValue([localValue[0], newMax]);
      onChange([localValue[0], newMax]);
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center gap-2">
        <label htmlFor="priceMin" className="text-sm text-gray-600">Alates:</label>
        <input
          id="priceMin"
          type="number"
          min={min}
          max={value[1]}
          value={localValue[0]}
          onChange={handleMinInputChange}
          aria-label="Miinimum hind"
          className="w-14 px-2 py-1 border rounded text-sm"
        />
        <label htmlFor="priceMax" className="text-sm text-gray-600">Kuni:</label>
        <input
          id="priceMax"
          type="number"
          min={value[0]}
          max={max}
          value={localValue[1]}
          onChange={handleMaxInputChange}
          aria-label="Maksimaalne hind"
          className="w-14 px-2 py-1 border rounded text-sm"
        />
      </div>
      
      <div 
        ref={sliderRef}
        className="relative h-2 bg-gray-200 rounded cursor-pointer"
        onClick={handleSliderClick}
      >
        <div 
          ref={trackRef}
          className="absolute h-full bg-[#4FC1E9] rounded"
        ></div>
        <div 
          ref={minThumbRef}
          className="absolute w-4 h-4 bg-white border-2 border-[#4FC1E9] rounded-full -mt-1 -ml-2 cursor-grab active:cursor-grabbing"
          onMouseDown={handleMinThumbMouseDown}
        ></div>
        <div 
          ref={maxThumbRef}
          className="absolute w-4 h-4 bg-white border-2 border-[#4FC1E9] rounded-full -mt-1 -ml-2 cursor-grab active:cursor-grabbing"
          onMouseDown={handleMaxThumbMouseDown}
        ></div>
      </div>
    </div>
  );
}
